"use client";

import { hashPosthogTraitsForSession } from "@repo/identity";
import { hasAnalyticsCaptureConsent } from "./analytics-consent-gate";
import { ActiveAnalyticsProvider } from "./resolve-provider";

const LAST_IDENTIFY_STORAGE_KEY = "sq:identity:last-identify";
const LINKED_STORAGE_KEY = "sq:identity:linked";

interface LastIdentifyState {
	sqid: string;
	traitHash: string;
}

function readLastIdentifyState(): LastIdentifyState | null {
	if (typeof window === "undefined") {
		return null;
	}
	const raw = window.sessionStorage.getItem(LAST_IDENTIFY_STORAGE_KEY);
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw) as LastIdentifyState;
	} catch {
		return null;
	}
}

function writeLastIdentifyState(state: LastIdentifyState): void {
	window.sessionStorage.setItem(
		LAST_IDENTIFY_STORAGE_KEY,
		JSON.stringify(state),
	);
}

export async function identifyPersonIfChanged(
	sqid: string,
	traits: Record<string, unknown>,
): Promise<void> {
	if (!hasAnalyticsCaptureConsent()) {
		return;
	}
	const traitHash = hashPosthogTraitsForSession(traits);
	const previous = readLastIdentifyState();
	if (previous?.sqid === sqid && previous.traitHash === traitHash) {
		return;
	}
	await ActiveAnalyticsProvider.identify?.(sqid, traits);
	writeLastIdentifyState({ sqid, traitHash });
}

export interface LinkSqidFromEmailDeepLinkResult {
	priorDistinctId: string | null;
	canonicalDistinctId: string;
}

export async function linkSqidFromEmailDeepLink(params: {
	sqid: string;
	traits: Record<string, unknown>;
	getCurrentDistinctId: () => Promise<string | null>;
}): Promise<LinkSqidFromEmailDeepLinkResult> {
	if (!hasAnalyticsCaptureConsent()) {
		return {
			priorDistinctId: null,
			canonicalDistinctId: params.sqid,
		};
	}
	const linkedKey = `${LINKED_STORAGE_KEY}:${params.sqid}`;
	if (window.sessionStorage.getItem(linkedKey) === "1") {
		await identifyPersonIfChanged(params.sqid, params.traits);
		return {
			priorDistinctId: null,
			canonicalDistinctId: params.sqid,
		};
	}

	const currentDistinctId = await params.getCurrentDistinctId();
	if (currentDistinctId && currentDistinctId !== params.sqid) {
		await ActiveAnalyticsProvider.alias?.(params.sqid, currentDistinctId);
	}
	await identifyPersonIfChanged(params.sqid, params.traits);
	window.sessionStorage.setItem(linkedKey, "1");
	return {
		priorDistinctId:
			currentDistinctId && currentDistinctId !== params.sqid
				? currentDistinctId
				: null,
		canonicalDistinctId: params.sqid,
	};
}

export function resetIdentityClientState(): void {
	ActiveAnalyticsProvider.reset?.();
	if (typeof window === "undefined") {
		return;
	}
	window.sessionStorage.removeItem(LAST_IDENTIFY_STORAGE_KEY);
	const linkedKeys: string[] = [];
	for (let index = 0; index < window.sessionStorage.length; index += 1) {
		const key = window.sessionStorage.key(index);
		if (key?.startsWith(`${LINKED_STORAGE_KEY}:`)) {
			linkedKeys.push(key);
		}
	}
	for (const key of linkedKeys) {
		window.sessionStorage.removeItem(key);
	}
}
